/**
 * Swiper for featured-collections product row (replaces theme scroll-carousel).
 * Lazy-inits swipers in tab panels (hidden panels use display:none until selected).
 *
 * Swiper 11: do not pass `pagination: undefined` / `navigation: undefined` — use
 * explicit `false` or the module can stay half-initialized (carousel only works
 * when pagination dots are enabled in the theme editor).
 */
(function () {
  const SWIPER_SELECTOR = "[data-featured-collections-swiper]";

  function readNumber(value, fallback) {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function readPositiveNumber(value, fallback) {
    const n = readNumber(value, NaN);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function parseOptions(el) {
    const d = el.dataset;
    return {
      speed: readPositiveNumber(d.fcSwiperSpeed, 400),
      loop: d.fcSwiperLoop === "true",
      centeredSlides: d.fcSwiperCentered === "true",
      autoplayDelay: Math.max(0, readNumber(d.fcSwiperAutoplayDelay, 0)),
      spaceBetweenMobile: Math.max(0, readNumber(d.fcSwiperSpaceMobile, 12)),
      spaceBetweenDesktop: Math.max(0, readNumber(d.fcSwiperSpaceDesktop, 24)),
      slidesPerViewMobile: readPositiveNumber(d.fcSwiperSpvMobile, 1.25),
      slidesPerViewDesktop: readPositiveNumber(d.fcSwiperSpvDesktop, 4),
      pagination: d.fcSwiperPagination === "true",
    };
  }

  function getSwiperConstructor() {
    const g = typeof globalThis !== "undefined" ? globalThis : window;
    return typeof g.Swiper === "function" ? g.Swiper : null;
  }

  function isRtl(el) {
    if (el?.getAttribute("dir") === "rtl") {
      return true;
    }
  }

  function progressScopeEl(swiperEl) {
    return (
      swiperEl.closest("[data-featured-collections-progress-scope]") ||
      swiperEl.closest(".featured-collections-carousel__item") ||
      swiperEl
    );
  }

  /** Same mapping as `assets/product-card-carousel.js` — smooth fill vs swiper.progress. */
  function updateProgressBar(swiper, scopeEl) {
    const bar = scopeEl?.querySelector(
      ".featured-collections-swiper__progress-bar",
    );
    if (!bar) {
      return;
    }
    const total = swiper.slides?.length ?? 0;
    if (total <= 0) {
      return;
    }
    const raw = typeof swiper.progress === "number" ? swiper.progress : 0;
    const p = total === 1 ? 1 : raw * ((total - 1) / total) + 1 / total;
    bar.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
  }

  function createSwiper(el) {
    const SwiperCtor = getSwiperConstructor();
    if (!SwiperCtor || el.swiper) {
      return el.swiper ?? null;
    }

    const host = el.closest("[data-featured-collections-swiper-host]");
    const prevBtn = host?.querySelector("[data-fc-swiper-prev] button");
    const nextBtn = host?.querySelector("[data-fc-swiper-next] button");
    const paginationEl = el.querySelector(
      ".featured-collections-swiper__pagination",
    );

    const o = parseOptions(el);

    const autoplay =
      o.autoplayDelay > 0
        ? {
            delay: o.autoplayDelay,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }
        : false;

    const navigation =
      prevBtn && nextBtn
        ? {
            prevEl: prevBtn,
            nextEl: nextBtn,
          }
        : false;

    const pagination =
      o.pagination && paginationEl
        ? {
            el: paginationEl,
            clickable: true,
          }
        : false;

    const progressScope = progressScopeEl(el);

    try {
      return new SwiperCtor(el, {
        rtl: isRtl(el),
        speed: o.speed,
        loop: o.loop,
        centeredSlides: o.centeredSlides,
        slidesPerView: o.slidesPerViewMobile,
        spaceBetween: o.spaceBetweenMobile,
        watchOverflow: false,
        watchSlidesProgress: true,
        forceToAxis: true,
        simulateTouch: true,
        grabCursor: true,
        noSwipingSelector:
          ".product-card__info, .product-card__swatches, .color-swatch, .product-card__swatch-more, .product-card__media--carousel:not(.product-card__media--carousel-static-mobile) .product-card__swiper, .product-card__carousel-nav",
        observer: false,
        observeParents: false,
        resizeObserver: true,
        navigation,
        pagination,
        autoplay,
        breakpoints: {
          1200: {
            slidesPerView: o.slidesPerViewDesktop,
            spaceBetween: o.spaceBetweenDesktop,
          },
        },
        on: {
          init(s) {
            updateProgressBar(s, progressScope);
          },
          progress(s) {
            updateProgressBar(s, progressScope);
          },
          slideChange(s) {
            updateProgressBar(s, progressScope);
          },
        },
      });
    } catch (err) {
      console.warn("[featured-collections] Swiper init failed:", err);
      return null;
    }
  }

  function initSwiperInCell(cell) {
    const els = cell.querySelectorAll(SWIPER_SELECTOR);
    if (!els.length) {
      return;
    }

    function run() {
      if (!getSwiperConstructor()) {
        return false;
      }
      requestAnimationFrame(() => {
        els.forEach((el) => {
          if (el.swiper) {
            return;
          }
          const swiper = createSwiper(el);
          swiper?.update?.();
          swiper?.navigation?.update?.();
          if (swiper) {
            updateProgressBar(swiper, progressScopeEl(el));
          }
        });
      });
      return true;
    }

    if (run()) {
      return;
    }

    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      if (run() || tries > 60) {
        window.clearInterval(id);
      }
    }, 50);
  }

  function initVisibleIfMissing() {
    document
      .querySelectorAll(".featured-collections-carousel__item.is-selected")
      .forEach((cell) => {
        const els = cell.querySelectorAll(SWIPER_SELECTOR);
        if ([...els].some((el) => !el.swiper)) {
          initSwiperInCell(cell);
        }
      });
  }

  function bindFeaturedCollectionsCarousels(root) {
    root.querySelectorAll("featured-collections-carousel").forEach((fc) => {
      if (fc.dataset.fcSwiperBound === "true") {
        return;
      }
      fc.dataset.fcSwiperBound = "true";
      fc.addEventListener("carousel:settle", (event) => {
        if (event.detail?.cell) {
          initSwiperInCell(event.detail.cell);
        }
      });
      const selected = fc.querySelector(
        ".featured-collections-carousel__item.is-selected",
      );
      if (selected) {
        initSwiperInCell(selected);
      }
    });
  }

  function initCartDrawerFeaturedSwipers(root = document) {
    root.querySelectorAll(".cart-drawer-featured-collection").forEach((el) => {
      initSwiperInCell(el);
    });
  }

  function initDynamicProductListSwipers(root = document) {
    root
      .querySelectorAll("product-recommendations, recently-viewed-products")
      .forEach((host) => {
        initSwiperInCell(host);
      });
  }

  function observeDynamicProductLists(root = document) {
    root
      .querySelectorAll("product-recommendations, recently-viewed-products")
      .forEach((host) => {
        if (host.dataset.fcSwiperObserved === "true") {
          return;
        }
        host.dataset.fcSwiperObserved = "true";
        const observer = new MutationObserver(() => {
          initSwiperInCell(host);
        });
        observer.observe(host, { childList: true, subtree: true });
      });
  }

  /**
   * Related products from metafields (you_may_also_like) render outside
   * <product-recommendations>, so dynamic init never runs — boot those hosts here.
   */
  function initStandaloneProductListSwipers(root = document) {
    root
      .querySelectorAll("[data-featured-collections-swiper-host]")
      .forEach((host) => {
        if (
          host.closest("featured-collections-carousel") ||
          host.closest("product-recommendations") ||
          host.closest("recently-viewed-products") ||
          host.closest(".cart-drawer-featured-collection")
        ) {
          return;
        }

        initSwiperInCell(host);
      });
  }

  function boot(root = document) {
    bindFeaturedCollectionsCarousels(root);
    initCartDrawerFeaturedSwipers(root);
    initDynamicProductListSwipers(root);
    observeDynamicProductLists(root);
    initStandaloneProductListSwipers(root);
  }

  function debounce(fn, ms) {
    let t;
    return (...args) => {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn(...args), ms);
    };
  }

  const refreshFeaturedCollectionsSwipers = debounce(() => {
    document.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
      el.swiper?.update?.();
      el.swiper?.navigation?.update?.();
      if (el.swiper) {
        updateProgressBar(el.swiper, progressScopeEl(el));
      }
    });
  }, 200);

  window.addEventListener("resize", refreshFeaturedCollectionsSwipers);

  function destroyInRoot(root) {
    root.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
      if (el.swiper && typeof el.swiper.destroy === "function") {
        el.swiper.destroy(true, true);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  window.addEventListener("load", initVisibleIfMissing);

  document.addEventListener("shopify:section:load", (event) => {
    if (
      event.target?.querySelector?.(SWIPER_SELECTOR) ||
      event.target?.querySelector?.(
        "product-recommendations, recently-viewed-products",
      )
    ) {
      boot(event.target);
      requestAnimationFrame(initVisibleIfMissing);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyInRoot(event.target);
  });

  document.addEventListener("cart-drawer:refreshed", (event) => {
    const cartDrawer = event.target?.closest?.("cart-drawer") ?? event.target;
    if (cartDrawer?.querySelector?.(SWIPER_SELECTOR)) {
      initCartDrawerFeaturedSwipers(cartDrawer);
      requestAnimationFrame(() => {
        cartDrawer.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
          el.swiper?.update?.();
          el.swiper?.navigation?.update?.();
          if (el.swiper) {
            updateProgressBar(el.swiper, progressScopeEl(el));
          }
        });
      });
    }
  });

  document.addEventListener(
    "dialog:after-show",
    (event) => {
      const cartDrawer = event.target?.closest?.("cart-drawer");
      if (cartDrawer) {
        initCartDrawerFeaturedSwipers(cartDrawer);
      }
    },
    true,
  );
})();
